// ============================================================
// sheetText - a character as plain text, to paste or print.
//
// For notes, a chat, a printed page: everything on the sheet a person
// would read, in sheet order, and how they stand when a state is given.
// Pure; nothing here is read back by the app.
// ============================================================
import type { Character } from './types';
import { characteristicBonus, getEffectiveUnnaturalMultipliers } from './combatStatsResolver';
import { collectSkillBonuses, getGrantedTalents } from './effectiveTalents';
import { talentText, traitText } from './instanceText';
import { getSkillRating } from './normalize';
import { CHARACTERISTICS, PACES, movementFor } from './sheetPatches';
import { SKILL_CATALOG } from './skillCatalog';
import { TIER_LABELS, skillTotal, tierLevel } from './skillTiers';
import { decodeCond } from '../conditions';
import type { CombatState } from '../roster/types';

const named = (name: string, parameter?: string) => (parameter ? `${name} (${parameter})` : name);

function characteristicsLine(sheet: Character): string {
  const effective = getEffectiveUnnaturalMultipliers(sheet);
  return CHARACTERISTICS.map(({ id, short }) => {
    const { value } = sheet.characteristics[id];
    const multiplier = effective[id] ?? 1;
    const bonus = characteristicBonus(value) * multiplier;
    return `${short} ${value} (${bonus}${multiplier > 1 ? ` x${multiplier}` : ''})`;
  }).join('  ');
}

function skillLines(sheet: Character): string[] {
  return SKILL_CATALOG.filter((def) => getSkillRating(sheet, def.id).trained).map((def) => {
    const rating = getSkillRating(sheet, def.id);
    const talents = collectSkillBonuses(sheet, def.id).reduce((sum, bonus) => sum + bonus.value, 0);
    const total = skillTotal(rating, sheet.characteristics[def.governing].value, talents, def.advanced);
    return `  ${def.name} ${TIER_LABELS[tierLevel(rating)].toLowerCase()}: ${total}`;
  });
}

/** A heading and its lines, or nothing when it has none. */
const block = (title: string, lines: string[]): string[] => (lines.length ? ['', title, ...lines] : []);

function armourLine(locations: Record<string, number>, worn?: Record<string, number>): string {
  return Object.entries(locations)
    .map(([loc, ap]) =>
      worn && worn[loc] !== undefined && worn[loc] !== ap ? `${loc} ${worn[loc]}/${ap}` : `${loc} ${ap}`,
    )
    .join(', ');
}

/** What a pin may say over the sheet: the armour as issued and the wounds maximum in force. */
export interface IssuedValues {
  armour?: Record<string, number>;
  woundsMax?: number;
}

/** "Magnitude 12 / 30" for a crowd, "Wounds 7 / 22" for anyone else; the maximum alone without a state. */
function healthLine(sheet: Character, state: CombatState | undefined, issued: IssuedValues): string {
  const crowd = !!sheet.horde?.isHorde;
  const max = crowd ? sheet.horde.maxMagnitude : (issued.woundsMax ?? sheet.wounds?.max ?? 0);
  const noun = crowd ? 'Magnitude' : 'Wounds';
  if (!state) return `${noun} ${max}`;
  return `${noun} ${crowd ? state.hordeMagnitude : state.woundsCurrent} / ${max}`;
}

/** How they stand: health, conditions, critical damage and rounds left. */
function conditionLines(sheet: Character, state: CombatState, issued: IssuedValues): string[] {
  const ammo = (sheet.weapons ?? [])
    .filter((w) => (state.weaponAmmo[w.id]?.max ?? 0) > 0)
    .map((w) => `${w.name} ${state.weaponAmmo[w.id].current}/${state.weaponAmmo[w.id].max}`);
  const listed = (title: string, codes: string[]) =>
    codes.length ? [`  ${title}: ${codes.map((c) => decodeCond(c) ?? c).join(', ')}`] : [];
  return [
    `  ${healthLine(sheet, state, issued)}`,
    ...listed('Conditions', state.conditions),
    ...listed('Critical damage', state.criticalDamage),
    ...(ammo.length ? [`  Ammunition: ${ammo.join(', ')}`] : []),
  ];
}

/**
 * The character as text.
 *
 * @param state when given, how they stand is added, and worn armour is
 *   shown beside the suit as issued ("body 6/10").
 * @param issued what the combatant's pins say over the sheet, so a pinned
 *   plate is not read as wear.
 */
export function characterToText(sheet: Character, state?: CombatState, issued: IssuedValues = {}): string {
  const armour = issued.armour ?? sheet.armour?.locations ?? {};
  const movement = sheet.movement ?? movementFor(sheet.characteristics.agi.value);
  const traits = (sheet.traits ?? []).map((t) => named(traitText(t)?.name ?? t.traitId, t.parameter));
  const talents = [...(sheet.talents ?? []), ...getGrantedTalents(sheet)].map((t) =>
    named(talentText(t)?.name ?? t.talentId, t.parameter),
  );
  const lines = [
    sheet.name || 'Unnamed',
    `${sheet.size}${sheet.xenos ? ', Xenos' : ''}`,
    '',
    characteristicsLine(sheet),
    `${healthLine(sheet, undefined, issued)}   Move ${PACES.map((p) => movement[p]).join('/')}`,
    `Armour${sheet.armour?.name ? ` (${sheet.armour.name})` : ''}: ${armourLine(armour, state?.armour)}`,
    ...block('Traits', traits.length ? [`  ${traits.join(', ')}`] : []),
    ...block('Talents', talents.length ? [`  ${talents.join(', ')}`] : []),
    ...block('Skills', skillLines(sheet)),
    ...block(
      'Weapons',
      (sheet.weapons ?? []).map((w) => `  ${w.name || 'Weapon'}`),
    ),
    ...(state ? block('As they stand', conditionLines(sheet, state, issued)) : []),
    ...(sheet.notes ? block('Notes', [`  ${sheet.notes}`]) : []),
  ];
  return lines.join('\n');
}
